import { Button } from "@/components/ui/button";
import {
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import { UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";
import {
  ExpenseCategoryGlPairing,
  useAdminUpsertExpenseCategoryGlPairingMutation,
} from "@common/utils/graphql";
import { useEffect, useState } from "react";
import { CategoryManualForm } from "./CategoryManualForm";
import { CategoryUploadSchema } from "./forms";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateRequest } from "@common/utils/rest/createRequest";
import { useToast } from "@/components/ui/use-toast";
import { CategoryUploadForm } from "./CategoryUploadForm";
import { validateForm } from "@common/components/utils/FormValidation";
import { CategoryFormSchema } from "../forms/Forms";
import {
  SheetTabsContainer,
  SheetTabsContent,
  SheetTabsList,
} from "@common/components";
import { hasDirtyFields } from "@/utils/utils";

interface Props {
  onBack: (category?: ExpenseCategoryGlPairing) => void;
  form: UseFormReturn<z.infer<typeof CategoryFormSchema>>;
  mode: "edit" | "create";
  category?: ExpenseCategoryGlPairing;
}

const SheetTabs = [
  {
    name: "Input Manually",
    value: "manual",
  },
  {
    name: "Upload from CSV",
    value: "upload",
  },
];

export const CreateEditCategorySheet = ({
  form,
  onBack,
  mode,
  category,
}: Props) => {
  const isEditMode = mode === "edit";

  const { createRequest } = useCreateRequest();
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const uploadForm = useForm<z.infer<typeof CategoryUploadSchema>>({
    resolver: zodResolver(CategoryUploadSchema),
  });

  const [upsertCategory, { loading }] =
    useAdminUpsertExpenseCategoryGlPairingMutation();

  useEffect(() => {
    // populate form if category is specified
    // TODO: Make this dynamic
    if (category) {
      form.reset({
        uuid: category.uuid,
        gl: category.glAccount!,
        name: category.categoryName,
        isVisible: category.isShown,
      });
    }
  }, [category, form]);

  usePreventSheetClose(() => {
    return hasDirtyFields(form);
  });

  const uploadCategoryCSV = async () => {
    const isFormValid = await validateForm(uploadForm);
    if (!isFormValid) {
      return;
    }

    setIsUploading(true);
    const { file } = uploadForm.getValues();
    const formData = new FormData();
    formData.append("file", file || "");

    try {
      const response = await createRequest(
        "expense/processExpenseCategoryGLPairings",
        "POST",
        formData,
      );
      if (response.status >= 400) {
        throw new Error();
      }
      toast({
        title: "CSV uploaded successfully",
      });
      onBack();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to upload CSV, please try again",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const createCategory = async (shouldEdit: boolean) => {
    const isFormValid = await validateForm(form);
    if (!isFormValid) {
      return;
    }

    const values = form.getValues();
    const mutationVars = {
      categoryName: values.name,
      glAccount: values.gl,
      isShown: values.isVisible,
    };

    try {
      if (shouldEdit) {
        const { data } = await upsertCategory({
          variables: {
            uuid: values.uuid || uuidv4(),
            ...mutationVars,
          },
        });
        if (
          data?.adminUpsertExpenseCategoryGLPairing.__typename !==
          "MutationAdminUpsertExpenseCategoryGLPairingSuccess"
        ) {
          throw new Error();
        }
      } else {
        const { data } = await upsertCategory({
          variables: {
            uuid: uuidv4(),
            ...mutationVars,
          },
        });
        if (
          data?.adminUpsertExpenseCategoryGLPairing.__typename !==
          "MutationAdminUpsertExpenseCategoryGLPairingSuccess"
        ) {
          throw new Error();
        }
      }
      onBack({
        ...mutationVars,
        updatedAt: new Date(),
      } as ExpenseCategoryGlPairing);
    } catch (e) {
      const title = shouldEdit
        ? "Failed to update category, please try again"
        : "Failed to create category, please try again";
      toast({
        title,
        variant: "destructive",
      });
    }
  };

  const header = (
    <SheetHeader>
      <SheetTitle>
        {isEditMode ? "Edit Category" : "Create Category"}
      </SheetTitle>
    </SheetHeader>
  );

  return (
    <>
      {header}
      <SheetTabsContainer defaultValue="manual">
        {!isEditMode && <SheetTabsList tabs={SheetTabs} />}

        {/* Input Manually Tab */}
        <SheetTabsContent value="manual">
          <CategoryManualForm form={form} loading={loading} />
          <div className="min-h-8 flex-1" />
          {isEditMode ? (
            <Button
              className="w-full"
              onClick={() => createCategory(true)}
              disabled={loading}
            >
              {loading ? "Saving Category" : "Save Category"}
            </Button>
          ) : (
            <Button
              className="w-full"
              onClick={() => createCategory(false)}
              disabled={loading}
            >
              {loading ? "Creating Category" : "Create Category"}
            </Button>
          )}
        </SheetTabsContent>

        {/* Upload CSV Tab */}
        <SheetTabsContent value="upload">
          <CategoryUploadForm form={uploadForm} loading={isUploading} />
          <div className="min-h-8 flex-1" />
          <Button
            className="w-full"
            onClick={uploadCategoryCSV}
            disabled={isUploading}
          >
            {isUploading ? "Uploading CSV" : "Upload CSV"}
          </Button>
        </SheetTabsContent>
      </SheetTabsContainer>
    </>
  );
};
